import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, Video, FileText, Headphones, Image } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import SortableList from '../../components/SortableList';

const AdminCourseEdit = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isNew = courseId === 'nuevo';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    long_description: '',
    price: '',
    currency: 'EUR',
    duration: '',
    image_url: '',
    is_published: true,
    hide_price: false,
    availability_status: 'disponible',
    modules: []
  });

  useEffect(() => {
    if (!isNew) {
      fetchCourse();
    }
  }, [courseId, isNew]);

  const fetchCourse = async () => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Error al cargar el curso');
      navigate('/admin/cursos');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleModuleChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      )
    }));
  };

  const addModule = () => {
    setFormData(prev => ({
      ...prev,
      modules: [
        ...prev.modules,
        { 
          id: `m${Date.now()}`, 
          title: '', 
          description: '', 
          content_type: 'video', 
          duration: '',
          content_url: '',  // URL para video/audio embebido
          content_text: '', // Texto/HTML para contenido escrito
          thumbnail_url: '' // Miniatura opcional
        }
      ]
    }));
  };

  const removeModule = (index) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.filter((_, i) => i !== index)
    }));
  };

  // Notion-style drag-drop reorder of modules (local-only; saved with main form submit)
  const reorderModules = (newModules) => {
    setFormData(prev => ({ ...prev, modules: newModules }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.price) {
      toast.error('Por favor, completa los campos obligatorios');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (isNew) {
        await api.post('/admin/courses', data);
        toast.success('Curso creado correctamente');
      } else {
        await api.put(`/admin/courses/${courseId}`, data);
        toast.success('Curso actualizado correctamente');
      }
      navigate('/admin/cursos');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar el curso');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-[hsl(var(--sage-light))]">
        <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to="/admin/cursos" 
            className="p-2 rounded-full hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[hsl(var(--text-secondary))]" strokeWidth={1.5} />
          </Link>
          <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))]">
            {isNew ? 'Nuevo Curso' : 'Editar Curso'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="card-soft space-y-6">
            <h2 className="text-xl font-heading text-[hsl(var(--text-primary))]">
              Información básica
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ej: Registros Akáshicos: Nivel 1"
                  className="input-soft"
                  data-testid="course-title-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="subtitle">Subtítulo</Label>
                <Input
                  id="subtitle"
                  name="subtitle"
                  value={formData.subtitle}
                  onChange={handleChange}
                  placeholder="Ej: Iniciación a la lectura de los Registros"
                  className="input-soft"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Descripción corta *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descripción breve que aparecerá en las tarjetas..."
                  className="input-soft min-h-[100px]"
                  data-testid="course-description-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="long_description">Descripción completa</Label>
                <Textarea
                  id="long_description"
                  name="long_description"
                  value={formData.long_description}
                  onChange={handleChange}
                  placeholder="Descripción detallada del curso..."
                  className="input-soft min-h-[150px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Precio (€) *</Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="297"
                  className="input-soft"
                  data-testid="course-price-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="duration">Duración</Label>
                <Input
                  id="duration"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="Ej: 8 semanas"
                  className="input-soft"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="image_url">URL de imagen</Label>
                <Input
                  id="image_url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="input-soft"
                />
              </div>

              <div className="flex items-center justify-between md:col-span-2 p-4 bg-[hsl(var(--sage-light))] rounded-xl">
                <div>
                  <Label htmlFor="is_published" className="cursor-pointer">Publicado</Label>
                  <p className="text-sm text-[hsl(var(--text-tertiary))]">
                    Los cursos no publicados no aparecen en el catálogo público
                  </p>
                </div>
                <Switch
                  id="is_published"
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
                />
              </div>

              {/* Availability Status */}
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="availability_status">Estado de disponibilidad</Label>
                <Select
                  value={formData.availability_status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, availability_status: value }))}
                >
                  <SelectTrigger className="input-soft">
                    <SelectValue placeholder="Selecciona estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponible">✅ Disponible</SelectItem>
                    <SelectItem value="proximamente">🕐 Próximamente</SelectItem>
                    <SelectItem value="no_disponible">❌ No disponible</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-[hsl(var(--text-tertiary))]">
                  Indica si el curso está disponible para compra
                </p>
              </div>

              {/* Hide Price */}
              <div className="flex items-center justify-between md:col-span-2 p-4 bg-[hsl(var(--sage-light))] rounded-xl">
                <div>
                  <Label htmlFor="hide_price" className="cursor-pointer">Ocultar precio</Label>
                  <p className="text-sm text-[hsl(var(--text-tertiary))]">
                    Oculta el precio en la web pública (útil para cursos aún no disponibles)
                  </p>
                </div>
                <Switch
                  id="hide_price"
                  checked={formData.hide_price}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hide_price: checked }))}
                />
              </div>
            </div>
          </div>

          {/* Modules */}
          <div className="card-soft space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading text-[hsl(var(--text-primary))]">
                Módulos del curso
              </h2>
              <Button type="button" onClick={addModule} className="btn-outline" size="sm">
                <Plus className="w-4 h-4 mr-2" /> Añadir módulo
              </Button>
            </div>

            {formData.modules.length === 0 ? (
              <div className="text-center py-8 text-[hsl(var(--text-tertiary))]">
                No hay módulos. Añade el primer módulo.
              </div>
            ) : (
              <SortableList
                items={formData.modules.map((m, i) => ({ ...m, id: m.id || `m-${i}` }))}
                onReorder={reorderModules}
                className="space-y-6"
                renderItem={(module, index, { dragHandleProps, isDragging }) => (
                  <div 
                    className={`p-6 border border-[hsl(var(--border))] rounded-xl bg-white ${isDragging ? 'shadow-2xl ring-2 ring-[hsl(var(--accent))]/30' : ''}`}
                  >
                    {/* Module Header */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-[hsl(var(--border))]">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          {...dragHandleProps}
                          className="p-1 -m-1 rounded text-[hsl(var(--text-tertiary))] hover:bg-[hsl(var(--sage-light))] cursor-grab active:cursor-grabbing touch-none transition-colors"
                          aria-label="Arrastrar para reordenar módulo"
                          data-testid={`drag-module-${index}`}
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <span className="font-heading text-lg text-[hsl(var(--accent))]">Módulo {index + 1}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeModule(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="space-y-2">
                        <Label>Título del módulo *</Label>
                        <Input
                          placeholder="Ej: Introducción a los Registros"
                          value={module.title}
                          onChange={(e) => handleModuleChange(index, 'title', e.target.value)}
                          className="input-soft"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Duración</Label>
                        <Input
                          placeholder="Ej: 45min"
                          value={module.duration}
                          onChange={(e) => handleModuleChange(index, 'duration', e.target.value)}
                          className="input-soft"
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Descripción breve</Label>
                        <Input
                          placeholder="Breve descripción de lo que se aprende en este módulo"
                          value={module.description}
                          onChange={(e) => handleModuleChange(index, 'description', e.target.value)}
                          className="input-soft"
                        />
                      </div>
                    </div>

                    {/* Content Type & Content */}
                    <div className="p-4 bg-[hsl(var(--sage-light))] rounded-xl space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Tipo de contenido</Label>
                          <Select
                            value={module.content_type || 'video'}
                            onValueChange={(value) => handleModuleChange(index, 'content_type', value)}
                          >
                            <SelectTrigger className="input-soft">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="video">
                                <div className="flex items-center gap-2">
                                  <Video className="w-4 h-4" /> Vídeo
                                </div>
                              </SelectItem>
                              <SelectItem value="audio">
                                <div className="flex items-center gap-2">
                                  <Headphones className="w-4 h-4" /> Audio
                                </div>
                              </SelectItem>
                              <SelectItem value="text">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4" /> Texto
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Miniatura (opcional)</Label>
                          <Input
                            placeholder="URL de imagen de portada"
                            value={module.thumbnail_url || ''}
                            onChange={(e) => handleModuleChange(index, 'thumbnail_url', e.target.value)}
                            className="input-soft"
                          />
                        </div>
                      </div>

                      {/* Content based on type */}
                      {(module.content_type === 'video' || module.content_type === 'audio') && (
                        <div className="space-y-2">
                          <Label>
                            {module.content_type === 'video' ? 'URL del vídeo' : 'URL del audio'}
                          </Label>
                          <Input
                            placeholder={module.content_type === 'video' 
                              ? "URL de YouTube, Vimeo o archivo de vídeo" 
                              : "URL del archivo de audio"}
                            value={module.content_url || ''}
                            onChange={(e) => handleModuleChange(index, 'content_url', e.target.value)}
                            className="input-soft"
                          />
                          <p className="text-xs text-[hsl(var(--text-tertiary))]">
                            {module.content_type === 'video' 
                              ? "Soporta: YouTube (youtube.com/watch?v=...), Vimeo (vimeo.com/...), o URL directa de vídeo"
                              : "URL directa al archivo de audio (mp3, wav, etc.)"}
                          </p>
                        </div>
                      )}

                      {module.content_type === 'text' && (
                        <div className="space-y-2">
                          <Label>Contenido de texto</Label>
                          <Textarea
                            placeholder="Escribe aquí el contenido del módulo. Puedes usar formato básico..."
                            value={module.content_text || ''}
                            onChange={(e) => handleModuleChange(index, 'content_text', e.target.value)}
                            className="input-soft min-h-[200px]"
                          />
                          <p className="text-xs text-[hsl(var(--text-tertiary))]">
                            El texto se mostrará formateado a los alumnos.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              />
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Link to="/admin/cursos">
              <Button type="button" variant="outline" className="btn-outline">
                Cancelar
              </Button>
            </Link>
            <Button 
              type="submit" 
              className="btn-gold"
              disabled={saving}
              data-testid="save-course-button"
            >
              {saving ? 'Guardando...' : isNew ? 'Crear curso' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCourseEdit;
